import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CarFront, Gauge, Fuel, Cog, Calendar } from "lucide-react";
import SEO from "../components/SEO";
import api, { imgUrl } from "../lib/api";
import { BUSINESS } from "../lib/site";

export const CAR_STATUS_STYLE = {
  AVAILABLE: "bg-emerald-600/10 text-emerald-700 border border-emerald-600/25",
  RESERVED: "bg-sky/10 text-brand border border-sky/30",
  SOLD: "bg-slate-500/10 text-slate-500 border border-slate-400/25",
  HIDDEN: "bg-slate-800/10 text-slate-600 border border-slate-500/25",
};

export default function CarsPage() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cars").then((res) => setCars(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO
        title="Cars For Sale in Adlington | Quality Used Cars - PR7 MOT Centre"
        description="Quality used cars for sale at PR7 MOT Centre, Margaret House, Huyton Road, Adlington PR7 4HD. Checked over by our own workshop. Call 01257 786 540."
        path="/cars-for-sale"
      />

      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0 eng-grid" />
        <div className="container-pr7 relative py-14 lg:py-16">
          <p className="eyebrow-light mb-3">Cars For Sale</p>
          <h1 className="font-heading font-black text-white text-4xl sm:text-5xl tracking-tight">Quality used cars</h1>
          <p className="text-pale/70 text-base leading-relaxed mt-4 max-w-2xl">
            Cars for sale at our Adlington workshop - checked over by the same technicians who service and MOT vehicles here every day. To arrange a viewing, call{" "}
            <a href={BUSINESS.phoneHref} className="text-sky font-semibold">{BUSINESS.phone}</a>.
          </p>
        </div>
      </section>

      <section className="section-pad bg-surface">
        <div className="container-pr7">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white border border-border rounded-lg h-80 animate-pulse" />
              ))}
            </div>
          ) : cars.length === 0 ? (
            <div data-testid="cars-empty" className="text-center py-20 bg-white border border-border rounded-lg max-w-2xl mx-auto">
              <CarFront className="mx-auto text-brand mb-5" size={48} strokeWidth={1.5} />
              <h2 className="font-heading font-extrabold text-ink text-2xl tracking-tight">No cars for sale right now</h2>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed max-w-md mx-auto">
                We don't have any vehicles listed at the moment, but stock changes regularly. Give us a call on{" "}
                <a href={BUSINESS.phoneHref} className="text-brand font-semibold">{BUSINESS.phone}</a> and we'll let you know what's coming in.
              </p>
            </div>
          ) : (
            <div data-testid="cars-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.map((c) => {
                const main = c.images?.[c.main_image] || c.images?.[0];
                return (
                  <Link
                    key={c.id}
                    to={`/cars-for-sale/${c.id}`}
                    data-testid={`car-card-${c.id}`}
                    className="group bg-white border border-border rounded-lg overflow-hidden flex flex-col hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
                  >
                    <div className="relative aspect-[16/10] bg-surface overflow-hidden">
                      {main ? (
                        <img src={imgUrl(main)} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground"><CarFront size={44} /></div>
                      )}
                      <span className={`absolute top-3 left-3 status-chip ${CAR_STATUS_STYLE[c.status] || CAR_STATUS_STYLE.AVAILABLE}`}>{c.status}</span>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-heading font-bold text-ink text-lg leading-snug">{c.title}</h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-xs text-muted-foreground">
                        {c.year && <span className="inline-flex items-center gap-1.5"><Calendar size={13} className="text-brand" />{c.year}</span>}
                        {c.mileage && <span className="inline-flex items-center gap-1.5"><Gauge size={13} className="text-brand" />{c.mileage}</span>}
                        {c.fuel && <span className="inline-flex items-center gap-1.5"><Fuel size={13} className="text-brand" />{c.fuel}</span>}
                        {c.transmission && <span className="inline-flex items-center gap-1.5"><Cog size={13} className="text-brand" />{c.transmission}</span>}
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border mt-auto">
                        <p className="font-mono font-semibold text-brand text-xl">£{Number(c.price).toLocaleString("en-GB")}</p>
                        <span className="inline-flex items-center gap-1.5 font-heading font-bold text-xs text-brand group-hover:text-sky tracking-wide transition-colors">
                          VIEW CAR <ArrowRight size={13} />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
